import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Autocomplete,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  type HTMLAttributes,
  memo,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import type { WorkflowListItem } from 'src/graphql/entities/workflow';
import { ActiveStatusDot } from 'src/pages/Settings/workflows/workflows-page.elements';
import type { WorkflowVersionRaw } from 'src/types';
import {
  getWorkflowVersionLabel,
  getWorkflowVersionOptionPrimaryLabel,
  getWorkflowVersionOptionSecondaryLabel,
} from './workflow-version-option-label';

dayjs.extend(relativeTime);

type WorkflowItem = Pick<WorkflowListItem, 'workflowId' | 'name' | 'isPlatform'>;

type WorkflowVersionItem = Pick<
  WorkflowVersionRaw,
  'workflowVersionId' | 'version' | 'isActive' | 'updatedAt' | 'description'
>;

type AutocompleteOptionProps = HTMLAttributes<HTMLLIElement> & { key: string };

interface WorkflowSelectionToolbarProps {
  workflows: WorkflowItem[];
  workflowVersions: WorkflowVersionItem[];
  selectedWorkflowId?: string;
  selectedVersionId?: string;
  workflowSelectDisabled: boolean;
  versionSelectDisabled: boolean;
  newDraftVersionDisabled: boolean;
  onWorkflowChange: (workflowId?: string) => void;
  onVersionChange: (workflowVersionId?: string) => void;
  onNewWorkflow: () => void;
  onNewDraftVersion: () => void;
  onOpenAssignments: () => void;
  versionActions?: ReactNode;
}

const dropdownWidthStyles = {
  width: { xs: '100%', sm: 220, md: 260 },
  maxWidth: 320,
};

const WorkflowSelectionToolbarComponent = ({
  workflows,
  workflowVersions,
  selectedWorkflowId,
  selectedVersionId,
  workflowSelectDisabled,
  versionSelectDisabled,
  newDraftVersionDisabled,
  onWorkflowChange,
  onVersionChange,
  onNewWorkflow,
  onNewDraftVersion,
  onOpenAssignments,
  versionActions,
}: WorkflowSelectionToolbarProps) => {
  const theme = useTheme();
  const [newMenuAnchor, setNewMenuAnchor] = useState<HTMLElement | null>(null);
  const closeNewMenu = useCallback(() => setNewMenuAnchor(null), []);
  const getRelativeTime = useCallback(
    (value?: string | null) => (value ? dayjs(value).fromNow() : '—'),
    [],
  );

  const workflowOptions = useMemo<WorkflowItem[]>(
    () =>
      workflows.map((workflow) => ({
        ...workflow,
        workflowId: String(workflow.workflowId),
      })),
    [workflows],
  );

  const versionOptions = useMemo<WorkflowVersionItem[]>(
    () =>
      workflowVersions.map((workflowVersion) => ({
        ...workflowVersion,
        workflowVersionId: String(workflowVersion.workflowVersionId),
      })),
    [workflowVersions],
  );

  const hasPlatformWorkflows = workflowOptions.some((workflow) => workflow.isPlatform);

  const workflowValue = useMemo<WorkflowItem | null>(
    () =>
      workflowOptions.find((workflow) => workflow.workflowId === selectedWorkflowId) ?? null,
    [selectedWorkflowId, workflowOptions],
  );

  const versionValue = useMemo<WorkflowVersionItem | null>(
    () =>
      versionOptions.find((workflowVersion) => {
        return workflowVersion.workflowVersionId === selectedVersionId;
      }) ?? null,
    [selectedVersionId, versionOptions],
  );

  const renderVersionOption = (option: WorkflowVersionItem) => (
    <Stack direction="row" alignItems="center" width="100%" spacing={1}>
      <Box sx={{ flexShrink: 0, width: 10, display: 'flex', justifyContent: 'center' }}>
        {!!option.isActive && <ActiveStatusDot aria-label="Active version" />}
      </Box>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={600} noWrap>
          {getWorkflowVersionOptionPrimaryLabel(option.description)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {getWorkflowVersionOptionSecondaryLabel(
            option.version,
            getRelativeTime(option.updatedAt),
          )}
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        sx={{
          width: '100%',
          alignItems: { xs: 'stretch', lg: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          flexWrap="wrap"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Autocomplete<WorkflowItem, false, boolean>
            value={workflowValue}
            onChange={(_, value) => onWorkflowChange(value?.workflowId)}
            disabled={workflowSelectDisabled}
            options={workflowOptions}
            groupBy={
              hasPlatformWorkflows
                ? (option) => (option.isPlatform ? 'Platform workflows' : 'Your workflows')
                : undefined
            }
            getOptionLabel={(option) => option.name ?? ''}
            isOptionEqualToValue={(option, value) => option.workflowId === value?.workflowId}
            noOptionsText={workflowSelectDisabled ? 'No workflows' : 'No matches'}
            sx={dropdownWidthStyles}
            renderInput={(params) => (
              <>
                <FormFieldLabel htmlFor={params.id}>Workflow</FormFieldLabel>
                <TextField {...params} placeholder="Select Workflow" size="small" />
              </>
            )}
            renderOption={(props: AutocompleteOptionProps, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Typography variant="body2" fontWeight={600}>
                    {option.name}
                  </Typography>
                </li>
              );
            }}
          />

          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ minWidth: 0 }}>
            <Autocomplete<WorkflowVersionItem, false, boolean>
              value={versionValue}
              onChange={(_, value) => onVersionChange(value?.workflowVersionId)}
              disabled={versionSelectDisabled}
              options={versionOptions}
              getOptionLabel={(option) => getWorkflowVersionLabel(option.version)}
              isOptionEqualToValue={(option, value) =>
                option.workflowVersionId === value?.workflowVersionId
              }
              noOptionsText={versionSelectDisabled ? 'Select workflow first' : 'No matches'}
              sx={dropdownWidthStyles}
              ListboxProps={{
                sx: { '& .MuiAutocomplete-option': { alignItems: 'flex-start' } },
              }}
              renderInput={(params) => (
                <>
                  <FormFieldLabel htmlFor={params.id}>Version</FormFieldLabel>
                  <TextField
                    {...params}
                    placeholder="Select Version"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: versionValue?.isActive ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <ActiveStatusDot
                            sx={{
                              boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                            }}
                          />
                          {params.InputProps.endAdornment}
                        </Stack>
                      ) : (
                        params.InputProps.endAdornment
                      ),
                    }}
                  />
                </>
              )}
              renderOption={(props: AutocompleteOptionProps, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    {renderVersionOption(option)}
                  </li>
                );
              }}
            />

            {versionActions}
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1}
          sx={{ alignSelf: { xs: 'stretch', lg: 'flex-end' } }}
        >
          {/* Assignments are per partner and per trigger, not per workflow, so this sits
              outside the workflow/version selects and stays enabled with nothing selected. */}
          <Button
            variant="outlined"
            onClick={onOpenAssignments}
            data-testid="workflows-assignments-open"
          >
            Assignments
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            aria-haspopup="menu"
            aria-expanded={newMenuAnchor ? 'true' : undefined}
            onClick={(event) => setNewMenuAnchor(event.currentTarget)}
            data-testid="workflows-new"
          >
            New
          </Button>
          <Menu anchorEl={newMenuAnchor} open={!!newMenuAnchor} onClose={closeNewMenu}>
            <MenuItem
              onClick={() => {
                closeNewMenu();
                onNewWorkflow();
              }}
              data-testid="workflows-new-workflow"
            >
              Workflow
            </MenuItem>
            {/* The id the old Create Draft Version button carried: it opens the same dialog. */}
            <MenuItem
              disabled={newDraftVersionDisabled}
              onClick={() => {
                closeNewMenu();
                onNewDraftVersion();
              }}
              data-testid="workflows-create-draft"
            >
              Draft version
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
    </Stack>
  );
};

WorkflowSelectionToolbarComponent.displayName = 'WorkflowSelectionToolbar';

export const WorkflowSelectionToolbar = memo(WorkflowSelectionToolbarComponent);
